import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fmtErr } from "../lib/api";
import { Lightning } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "", org_name: "" });

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const data = await login(loginForm.email, loginForm.password);
      nav(data?.must_change_password ? "/set-password" : "/dashboard");
    } catch (err) { setError(fmtErr(err)); } finally { setBusy(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await register(regForm);
      nav("/onboarding");
    } catch (err) { setError(fmtErr(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--c-bg)] flex" data-testid="login-page">
      <div className="hidden lg:block relative w-[46%] overflow-hidden bg-[var(--c-bg)]">
        <div className="absolute -left-24 -top-24 w-[520px] h-[520px] rounded-full bg-[var(--c-accent)]/15 blur-[120px]" />
        <div className="absolute right-0 bottom-0 w-[360px] h-[360px] rounded-full bg-[var(--c-ember)]/10 blur-[110px]" />
        <div className="absolute inset-y-0 right-0 w-px ember-rule opacity-60" />
        <div className="relative h-full flex flex-col justify-between p-10">
          <div className="flex items-center gap-2.5">
            <div className="relative w-8 h-8 rounded-md bg-[var(--c-surface)] border border-[var(--c-border)] flex items-center justify-center overflow-hidden">
              <span className="absolute inset-x-0 bottom-0 h-[2px] bg-[var(--c-ember)]" />
              <Lightning size={17} weight="fill" color="var(--c-ember)" />
            </div>
            <div>
              <div className="text-lg font-semibold tracking-tight text-[var(--c-text)] font-heading leading-none">Formigen</div>
              <div className="text-[10px] font-mono tracking-[0.2em] text-[var(--c-muted)] uppercase mt-1">Product Design · Capexara</div>
            </div>
          </div>
          <div>
            <h2 className="text-4xl tracking-tight font-semibold text-[var(--c-text)] leading-[1.1] max-w-md font-heading">
              Design engineering,<br />
              <span className="text-[var(--c-muted)]">automated end to end.</span>
            </h2>
            <p className="text-sm text-[var(--c-muted)] mt-5 max-w-md leading-relaxed">
              Drop in a tender in any format. AI reads the specification, fills every parameter, runs the
              calculations, validates against IEC / IEEE / IS and generates drawings and BOMs.
            </p>
            <div className="mt-9 grid grid-cols-3 gap-px bg-[var(--c-border)] border border-[var(--c-border)] rounded-md overflow-hidden max-w-md">
              {[["27", "Industry packs"], ["154", "Equipment types"], ["11", "Modules"]].map(([v, l]) => (
                <div key={l} className="bg-[var(--c-surface)] px-4 py-3">
                  <div className="font-mono text-xl text-[var(--c-text)]">{v}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[var(--c-muted)] mt-0.5">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="relative w-8 h-8 rounded-md bg-[var(--c-surface)] border border-[var(--c-border)] flex items-center justify-center overflow-hidden">
              <span className="absolute inset-x-0 bottom-0 h-[2px] bg-[var(--c-ember)]" />
              <Lightning size={17} weight="fill" color="var(--c-ember)" />
            </div>
            <span className="text-lg font-semibold text-[var(--c-text)]">Formigen</span>
          </div>
          <Tabs defaultValue="login" onValueChange={() => setError("")}>
            <TabsList className="bg-[var(--c-surface)] border border-[var(--c-border)] w-full">
              <TabsTrigger value="login" className="flex-1" data-testid="login-tab">Sign in</TabsTrigger>
              <TabsTrigger value="register" className="flex-1" data-testid="register-tab">Create workspace</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              <form onSubmit={doLogin} className="space-y-4 mt-6" data-testid="login-form">
                <div className="space-y-1.5">
                  <Label className="text-[var(--c-muted)] text-xs">Email</Label>
                  <Input data-testid="login-email" type="email" required value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" placeholder="you@company.com" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[var(--c-muted)] text-xs">Password</Label>
                  <Input data-testid="login-password" type="password" required value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" placeholder="••••••••" />
                </div>
                {error && <div data-testid="login-error" className="text-xs text-[var(--c-red)] border border-[var(--c-red)]/40 bg-[var(--c-red)]/10 rounded-sm px-3 py-2">{error}</div>}
                <Button data-testid="login-submit-btn" disabled={busy} className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-md transition-colors duration-200">
                  {busy ? "Signing in…" : "Sign in"}
                </Button>
              </form>
            </TabsContent>
            <TabsContent value="register">
              <form onSubmit={doRegister} className="space-y-4 mt-6" data-testid="register-form">
                <div className="space-y-1.5">
                  <Label className="text-[var(--c-muted)] text-xs">Organization name</Label>
                  <Input data-testid="register-org" required value={regForm.org_name}
                    onChange={(e) => setRegForm({ ...regForm, org_name: e.target.value })}
                    className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" placeholder="Acme Manufacturing Ltd." />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[var(--c-muted)] text-xs">Your name</Label>
                  <Input data-testid="register-name" required value={regForm.name}
                    onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                    className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" placeholder="Jane Engineer" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[var(--c-muted)] text-xs">Email</Label>
                  <Input data-testid="register-email" type="email" required value={regForm.email}
                    onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                    className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" placeholder="you@company.com" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[var(--c-muted)] text-xs">Password</Label>
                  <Input data-testid="register-password" type="password" required minLength={6} value={regForm.password}
                    onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                    className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" placeholder="Min. 6 characters" />
                </div>
                {error && <div data-testid="register-error" className="text-xs text-[var(--c-red)] border border-[var(--c-red)]/40 bg-[var(--c-red)]/10 rounded-sm px-3 py-2">{error}</div>}
                <Button data-testid="register-submit-btn" disabled={busy} className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-md transition-colors duration-200">
                  {busy ? "Creating…" : "Create workspace"}
                </Button>
                <p className="text-[11px] text-[var(--c-muted)] leading-relaxed">You will become the workspace admin and configure industries, modules and workflow next.</p>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
